// Whether a figure corrected in the meeting has reached the questionnaire.
//
// The editors under a figure write to the questionnaire's own fields, and the
// questionnaire's autosave carries them to Salesforce. That autosave already
// knows when it is waiting, when it has saved, and when Salesforce refused
// something or could not be reached, but only the questionnaire screen used to
// say so. The assessment said "Changes are saved to the questionnaire" whatever
// had happened (QA review, 2026-09-30). This reads the same save state
// the questionnaire's banners read, and says it in the meeting's terms: there
// is no Save button or highlighted field in the meeting to point at.
//
// Drawings and the other assessment choices are saved separately, by the
// assessment sync, and are described by describeSync; this is only the
// questionnaire.

const plural = (n, one, many) => (n === 1 ? one : many);

function listOf(labels) {
  if (labels.length <= 1) return labels[0] || '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * `problem` is true when an edit made now would not reach Salesforce, which
 * is what the meeting shows above the slide as well as in the open card.
 */
export function describeProfileSave({
  demo = false,
  readOnly = false,
  noReference = false,
  loadError = null,
  coedit = null,
  autosaveStatus = 'idle',
  saveError = null,
  saveErrorKind = null,
  quarantined = [],
} = {}) {
  if (demo) {
    return { tone: 'quiet', problem: false, text: 'Sample household: changes stay in this browser and are never sent to Salesforce.' };
  }
  if (readOnly) {
    return { tone: 'quiet', problem: false, text: 'View only: nothing here is changed or saved.' };
  }
  if (loadError) {
    return {
      tone: 'warn',
      problem: true,
      text: "Not saved: this client's record did not load, so changes made here cannot reach Salesforce. Keep this tab open and submit a ticket to IT.",
    };
  }
  if (noReference) {
    return {
      tone: 'warn',
      problem: true,
      text: "Not saved: this was opened without a client record, so changes stay in this browser. Open it from the client's record in Salesforce.",
    };
  }
  if (coedit === 'secondary') {
    return {
      tone: 'warn',
      problem: true,
      text: 'Not saved: this client is open in another tab, so changes here are not sent. Make them in that tab, or close it and reload this one.',
    };
  }
  // Salesforce refused a save because the client was saved elsewhere after
  // this page opened it. The autosave then rests at idle, which would
  // otherwise read as everything saved.
  if (coedit === 'stale') {
    return {
      tone: 'warn',
      problem: true,
      text: 'Not saved: this client was saved from another tab or device after this page opened it, so saving has stopped here rather than overwrite that work. Go back to the questionnaire and load the latest version.',
    };
  }
  if (coedit === 'conflict') {
    return {
      tone: 'warn',
      problem: true,
      text: 'Not saved: this client was just saved from another tab, so saving is paused here. Reload to continue from the latest version.',
    };
  }
  if (saveErrorKind === 'quarantined') {
    const labels = (quarantined || []).map(q => q.label).filter(Boolean);
    const named = labels.length ? listOf(labels) : 'one entry';
    return {
      tone: 'warn',
      problem: true,
      text: `Salesforce would not accept what was entered in ${named}, so ${plural(labels.length || 1, 'that value is', 'those values are')} held back. Everything else is saved. Correct it and it saves on its own.`,
    };
  }
  if (autosaveStatus === 'blocked' || saveErrorKind === 'required') {
    return {
      tone: 'warn',
      problem: true,
      text: "Not saved yet: nothing reaches Salesforce until the client's first and last name are in the questionnaire. Everything is kept in this browser until then.",
    };
  }
  if (autosaveStatus === 'error') {
    if (saveErrorKind === 'validation') {
      return {
        tone: 'warn',
        problem: true,
        text: 'Not saved: Salesforce did not accept an entry. Check that amounts are numbers and dates are real dates; it saves on its own once corrected.',
        detail: saveError || null,
      };
    }
    return {
      tone: 'warn',
      problem: true,
      text: 'Not saved yet: Salesforce could not be reached. Nothing is lost while this tab stays open, and the app keeps retrying on its own.',
      detail: saveError || null,
    };
  }
  if (autosaveStatus === 'saving') {
    return { tone: 'quiet', problem: false, text: 'Saving to the questionnaire...' };
  }
  if (autosaveStatus === 'saved') {
    return { tone: 'ok', problem: false, text: 'Saved to the questionnaire.' };
  }
  // Idle: nothing is waiting to be sent, so the questionnaire in Salesforce
  // matches what is on screen.
  return { tone: 'quiet', problem: false, text: 'Everything here is saved to the questionnaire.' };
}
